import { Component, Input, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getOpenState, getWeeklySchedule, OpenState } from 'common_library';
import { MainStateService } from 'src/app/services/state/app-main-state.service';

interface OpeningHoursVm {
  cls: string; // status css class suffix: open | soon | chargeable | closed
  icon: string; // ionicon name
  line: string; // localized status line
  hint?: string; // longer explanation (full mode)
  twentyfourseven: boolean;
  todayWeekday: number; // 1-7 Mon..Sun, in the station timezone
}

/**
 * Opening-hours block for the selected installation.
 * - mode="compact": one status line (station toast).
 * - mode="full": status line + hint + weekly schedule with today highlighted (detail modal).
 *
 * Renders nothing when the installation has no opening-hours data (never assumes 24/7).
 * Reads the installation straight from MainStateService, like station-info.
 */
@Component({
  selector: 'yf-opening-hours',
  templateUrl: './opening-hours.component.html',
  styleUrls: ['./opening-hours.component.scss'],
  standalone: false,
})
export class OpeningHoursComponent {
  /** compact = one status line (toast); full = collapsible status header + weekly table (detail). */
  @Input() mode: 'compact' | 'full' = 'compact';

  /** full mode: whether the weekly table is expanded. */
  expanded = false;

  toggle(): void {
    this.expanded = !this.expanded;
  }

  // ponytail: computed samples new Date() at each recompute — fine for a transient
  // panel. No ticking timer (YAGNI); reopening the panel refreshes the state.
  readonly $vm = computed<OpeningHoursVm | null>(() => {
    const inst = this.mainStateService.$selectedInstallation();
    if (!inst?.openingTimes) return null; // unknown -> render nothing
    const state = getOpenState(inst.openingTimes, inst.chargingWhenClosed, inst.timeZone);
    if (state.status === 'unknown') return null;
    const presented = this.present(state);
    return {
      ...presented,
      twentyfourseven: inst.openingTimes.twentyfourseven === true,
      todayWeekday: state.weekday,
    };
  });

  readonly $week = computed(() =>
    getWeeklySchedule(this.mainStateService.$selectedInstallation()?.openingTimes),
  );

  constructor(public mainStateService: MainStateService, private translate: TranslateService) {}

  dayShort(weekday: number): string {
    return this.translate.instant('OPENING_HOURS.DAYS_SHORT.' + weekday);
  }

  formatPeriods(periods: Array<{ begin: string; end: string }>): string {
    return periods.map((p) => `${p.begin}–${p.end}`).join(', ');
  }

  private present(s: OpenState): { cls: string; icon: string; line: string; hint?: string } {
    const t = (k: string, p?: object) => this.translate.instant('OPENING_HOURS.' + k, p);
    switch (s.status) {
      case 'always':
        return { cls: 'open', icon: 'time-outline', line: t('OPEN_24H') };
      case 'open':
        return { cls: 'open', icon: 'time-outline', line: t('OPEN_UNTIL', { time: s.closesAt }) };
      case 'closingSoon':
        return { cls: 'soon', icon: 'time-outline', line: t('CLOSING_SOON', { time: s.closesAt }) };
      case 'closedButChargeable':
        return {
          cls: 'chargeable',
          icon: 'flash-outline',
          line: t('CLOSED_CHARGEABLE'),
          hint: t('CHARGEABLE_HINT'),
        };
      case 'closedBlocked':
      default:
        return {
          cls: 'closed',
          icon: 'alert-circle-outline',
          line: this.closedLine(s),
          hint: t('NOT_ACCESSIBLE_HINT'),
        };
    }
  }

  private closedLine(s: OpenState): string {
    const t = (k: string, p?: object) => this.translate.instant('OPENING_HOURS.' + k, p);
    if (!s.opensAt) return t('CLOSED');
    const { dayOffset, time, weekday } = s.opensAt;
    if (dayOffset === 0) return t('CLOSED_OPENS_TODAY', { time });
    if (dayOffset === 1) return t('CLOSED_OPENS_TOMORROW', { time });
    return t('CLOSED_OPENS_ON', { day: this.translate.instant('OPENING_HOURS.DAYS.' + weekday), time });
  }
}
